import { User } from '@microsoft/microsoft-graph-types-beta';
import {
    TimelineElement,
    CombinedDeviation,
    BaselineDeviation,
    Baseline,
    BaselinePostureCountsByDate,
} from '../public-api';

export function groupBy(arr: Array<any>, key: string, singleValueObj: boolean = false) {
    return arr.reduce((acc, obj) => {
        const _key = obj[key];
        acc[_key] = singleValueObj ? obj : [...(acc[_key] || []), obj];
        return acc;
    }, {});
}

export function getPostureControlsInThisPeriod(
    baselineDeviations: CombinedDeviation[],
    baselines: Baseline[],
    type: 'tenant' | 'group',
    start: string,
    end: string,
): BaselinePostureCountsByDate {
    const formatted: BaselinePostureCountsByDate = {};
    const dates = getDates(start, end);
    const initCreatedTimestamp = '2022-09-27';

    for (const date of dates) {
        const existingBaselines = baselines.filter((baseline) => {
            const createdDate = baseline.created.split('T')[0];
            return baseline.type === type && createdDate <= date;
        });

        const deviationsOfTheDate = getBaselineDeviationsCountOfTheDate(baselineDeviations, type, date);
        const complianceCountOfTheDate =
            date >= initCreatedTimestamp
                ? getBaselineComplianceCountOfTheDate(deviationsOfTheDate, existingBaselines, type, date)
                : null;

        const monitoredCountOfTheDate = date >= initCreatedTimestamp ? existingBaselines.length : null;

        formatted[date] = {
            deviating: deviationsOfTheDate.length,
            compliant: complianceCountOfTheDate,
            monitored: monitoredCountOfTheDate,
        };
    }

    return formatted;
}
// maybe wrong logic to get deviating baselines of the date
function getBaselineDeviationsCountOfTheDate(
    baselineDeviations: CombinedDeviation[],
    type: 'tenant' | 'group',
    date: any,
) {
    const deviatingBaselines = [];

    for (const bd of baselineDeviations) {
        if (
            bd.type !== type ||
            bd.deviation_detect_time.split('T')[0] > date ||
            (bd.deviation_resolve_time !== null && bd.deviation_resolve_time.split('T')[0] <= date)
        ) {
            continue;
        }

        let foundIndex = -1;
        for (let i = 0; i < deviatingBaselines.length; i++) {
            if (deviatingBaselines[i].baseline_id === bd.baseline_id) {
                foundIndex = i;
                break;
            }
        }

        if (foundIndex === -1) {
            deviatingBaselines.push(bd);
        }
    }

    return deviatingBaselines.sort((a, b) => b.deviation_detect_time.localeCompare(a.deviation_detect_time));
}

// maybe wrong logic to get passing baselines of the date
function getBaselineComplianceCountOfTheDate(
    deviating_baselines: CombinedDeviation[],
    baselines: Baseline[],
    type: 'tenant' | 'group',
    date: any,
) {
    let compliance_count = 0;
    if (baselines.length > 0) {
        for (const existing_baseline of baselines) {
            const is_deviated = deviating_baselines.some((d) => d.baseline_id === existing_baseline.id);
            if (!is_deviated) {
                compliance_count++;
            }
        }
    }
    return compliance_count;
}

function getDates(startDate: string, endDate: string): string[] {
    const dateArray = [];
    let currentDate = startDate.split('T')[0];
    while (currentDate <= endDate) {
        dateArray.push(currentDate);
        currentDate = addDays(currentDate, 1);
    }
    return dateArray;
}

function addDays(currentDate: string, days: number) {
    const date = new Date(currentDate.valueOf());
    date.setTime(date.getTime() + days * 24 * 3600 * 1000);
    return date.toISOString().split('T')[0];
}

// Logic has reviewed
export function GroupBaselineDeviationWithTimelineElementsByBaseline(
    array: CombinedDeviation[],
    users: User[],
): BaselineDeviation[] {
    const transformedArray = array.reduce(
        (transformedArray: BaselineDeviation[], currentDeviation: CombinedDeviation) => {
            const timelineElements: TimelineElement[] = [];
            const foundKeyIndex = getFoundKeyIndex(transformedArray, currentDeviation);
            const foundKeyTimeElements = foundKeyIndex !== -1 ? transformedArray[foundKeyIndex].timelineElements : [];
            const CreatedTime = getBaselineCreatedTimestamp(foundKeyIndex, transformedArray, currentDeviation);

            if (CreatedTime) {
                timelineElements.push(addTimeToBaselineTimeline(CreatedTime, 'created'));
            }
            timelineElements.push(addTimeToBaselineTimeline(currentDeviation['deviation_detect_time'], 'deviation'));

            if (currentDeviation['deviation_resolve_time']) {
                timelineElements.push(
                    addTimeToBaselineTimeline(currentDeviation['deviation_resolve_time'], 'remediated'),
                );
            }

            if (foundKeyIndex !== -1) {
                transformedArray[foundKeyIndex].timelineElements = [
                    ...transformedArray[foundKeyIndex].timelineElements,
                    ...timelineElements,
                ].sort((a: any, b: any) => a.date.toISOString().localeCompare(b.date.toISOString()));
            } else {
                const transformedItem: BaselineDeviation = setTransformedItem(
                    currentDeviation,
                    foundKeyTimeElements,
                    timelineElements,
                    users,
                );
                transformedArray.push(transformedItem);
            }
            return transformedArray;
        },
        [],
    );

    return transformedArray.map((item) => {
        return {
            ...item,
            status: setBaselineStatus(item.timelineElements[item.timelineElements.length - 1]),
        };
    });
}

export function setBaselineStatus(lastTimeElement: TimelineElement) {
    switch (lastTimeElement.status) {
        case 'deviation':
            return 'deviating';
        default:
            return 'pass';
    }
}

export function findUserNameById(user_id: string | null, users: User[]) {
    if (user_id === null) {
        return 'User no longer exists';
    }
    return users.find((user) => user_id === user.id)?.userPrincipalName ?? 'User no longer exists';
}

function getFoundKeyIndex(hash: BaselineDeviation[], curr: CombinedDeviation) {
    let foundKeyIndex = hash.findIndex((transformed_item: any) => transformed_item.baseline_id === curr.baseline_id);
    if (!!curr.group_name && !!curr.user_id) {
        foundKeyIndex = hash.findIndex(
            (transformed_item: any) =>
                transformed_item.baseline_id === curr.baseline_id &&
                transformed_item.group_name === curr.group_name &&
                transformed_item.user_id === curr.user_id,
        );
    }
    return foundKeyIndex;
}

/**
 * @param  {number} foundKeyIndex the target index of the grouped transformed_array array
 * @param  {any} tansformed the grouped tansformed array
 * @param  {any} curr the current element in the original array
 */
function getBaselineCreatedTimestamp(
    foundKeyIndex: number,
    transformed_array: BaselineDeviation[],
    curr: CombinedDeviation,
) {
    let created = undefined;
    if (
        foundKeyIndex === -1 ||
        (foundKeyIndex !== -1 &&
            !transformed_array[foundKeyIndex].timelineElements.find((el: TimelineElement) => el.status === 'created'))
    ) {
        if (curr['baseline_created'] > curr['deviation_detect_time']) {
            // created = '2022-01-01T00:00:00.000Z';
            return created;
        }
        created = curr['baseline_created'];
    }
    return created;
}
/**
 * @param  {string} time - the timestamp in string which will be added as date in the timeline element
 * @param  {'created'|'deviation'|'remediated'} status what is the type that the timestamp referring to
 */
function addTimeToBaselineTimeline(time: string, status: 'created' | 'deviation' | 'remediated') {
    const timelineElement = {
        date: new Date(time),
        status,
    };
    return timelineElement;
}
/**
 * @param  {CombinedDeviation} curr the current baseline deviation element in the original array
 * @param  {TimelineElement[]} existing_timeline_elements the existing timeline elements from transformed array
 * @param  {TimelineElement[]} new_timeline_elements the timeline elements to be added to the transformed array
 */
function setTransformedItem(
    curr: CombinedDeviation,
    existing_timeline_elements: TimelineElement[],
    new_timeline_elements: TimelineElement[],
    users: User[],
): BaselineDeviation {
    let transformed_item: BaselineDeviation = {
        category: curr.category,
        type: curr.type,
        name: curr.name,
        baseline_name: curr.baseline_name,
        baseline_id: curr.baseline_id,
        timelineElements: [...(existing_timeline_elements || []), ...new_timeline_elements].sort((a: any, b: any) =>
            a.date.toISOString().localeCompare(b.date.toISOString()),
        ),
    };

    if (curr.group_name && curr.user_id) {
        const user = users.find((u) => u.id === curr.user_id)!;
        if (!user || !user.userPrincipalName) {
            transformed_item = updateTransformedItemForGroupUserBaseline(
                transformed_item,
                curr.group_name,
                curr.user_id,
                'User no longer exists',
            );
        } else {
            transformed_item = updateTransformedItemForGroupUserBaseline(
                transformed_item,
                curr.group_name,
                curr.user_id,
                user.userPrincipalName!,
            );
        }
    }
    return transformed_item;
}

function updateTransformedItemForGroupUserBaseline(
    transformed_item: BaselineDeviation,
    group_name: string,
    user_id: string,
    user_name: string,
): BaselineDeviation {
    return {
        ...transformed_item,
        group_name,
        user_id,
        user_name,
    } as BaselineDeviation;
}
